import React, { useEffect } from "react";
import {
  Navigate,
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";

import { LinkProvider } from "decodable-ui/lib/components/LinkContext";
import { ThemeProvider } from "decodable-ui/lib/themes";

import { route as accessRoute } from "../access";
import { SetupIntentCallback } from "../accounts/components/SetupIntentCallback";
import { AccountsProvider } from "../accounts/hooks/useAccounts";
import { routes as connectionRoutes } from "../connections";
import { AuthProvider, useAuth } from "../hooks/useAuth";
import { AppQueryClientProvider } from "../hooks/useQueryClient";
import { routes as pipelineRoutes, flinkUiRoute } from "../pipelines";
import { routes as secretRoutes } from "../secrets";
import { routes as streamRoutes } from "../streams";
import { CommandPaletteProvider } from "./CommandPalette";
import Fallback from "./Fallback";
import GTagProvider, { GTagUserTracker } from "./GTagProvider";
import HomeButton from "./HomeButton";
import HubSpotProvider from "./HubSpotProvider";
import LoginPage from "./Login";
import SnackbarProvider from "./SnackbarProvider";
import TopBar from "./TopBar";
import { RouterLinkAdapter } from "./RouterLinkAdapter";
import { ErrorBoundaryFallback } from "./ErrorBoundaryFallback";
import AuthError from "./AuthError";

const LazyAccountScopedRoutes = React.lazy(
  () => import("./AccountScopedRoutes")
);

import { ExperimentsProvider } from "./experiments";
const LazyExperimentsPage = React.lazy(() => import("./experiments"));
const LazyAccountUpgradePage = React.lazy(
  () => import("../accounts/components/AccountUpgradePage")
);
const LazyInviteCallbackPage = React.lazy(
  () => import("../accounts/components/InviteCallback")
);
const LazyCliAuthCallbackPage = React.lazy(() => import("./CliAuthCallback"));
const LazyAccountScreen = React.lazy(
  () => import("../accounts/components/AccountScreen")
);
const LazyHomePage = React.lazy(() => import("./HomePage"));

import Accounts from "../accounts";
import Shell from "./Shell";
import { AppEventsProvider } from "../hooks/useAppEvents";
import { AgentProvider, useAgent } from "../hooks/useAgent";
import { LazyLoadingFallback } from "./LazyLoadingFallback";

export default function App() {
  return (
    <AgentProvider>
      <AppEventsProvider>
        <AuthProvider>
          <LinkProvider value={RouterLinkAdapter}>
            <ThemeProvider>
              <SnackbarProvider>
                <AppQueryClientProvider>
                  {/* Temporarily disabled. It's only showing up in dev, rather than local where it should be, anyway.
                   <ReactQueryDevtools
                     initialIsOpen={false}
                     position="bottom-right"
                    />
                 */}
                  <AccountsProvider>
                    <ExperimentsProvider>
                      <RootRouter />
                    </ExperimentsProvider>
                  </AccountsProvider>
                </AppQueryClientProvider>
              </SnackbarProvider>
            </ThemeProvider>
          </LinkProvider>
        </AuthProvider>
      </AppEventsProvider>
    </AgentProvider>
  );
}

function RootRouter() {
  const { user, isLoading } = useAuth();
  const agent = useAgent();
  useEffect(() => {
    if (user && agent) {
      agent.setUser({ id: user.sub, email: user.email });
    }
  }, [user, agent]);

  if (isLoading) {
    return <LazyLoadingFallback />;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <Router>
        <CommandPaletteProvider>
          <HubSpotProvider>
            <GTagProvider>
              <GTagUserTracker />
              <AppRoutes />
            </GTagProvider>
          </HubSpotProvider>
        </CommandPaletteProvider>
      </Router>
    </ErrorBoundary>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/-/accounts" replace />} />
      <Route path="/callback/cli/auth" element={<LazyCliAuthCallbackPage />} />
      <Route path="/callback/invite" element={<LazyInviteCallbackPage />} />
      <Route path="/-/">
        <Route path="experiments" element={<LazyExperimentsPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="callback/auth" element={<AuthCallback />} />
        <Route path="accounts/*" element={<Accounts />} />
        <Route path="auth-error" element={<AuthError />} />
      </Route>
      <Route path=":accountName" element={<LazyAccountScopedRoutes />}>
        <Route element={<Shell />}>
          <Route index element={<LazyHomePage />} />
          {accessRoute}
          <Route path="account" element={<LazyAccountScreen />} />
          <Route path="billing" element={<LazyAccountUpgradePage />} />
          <Route path="billing/confirm" element={<SetupIntentCallback />} />
          <Route path="connections">{connectionRoutes}</Route>
          <Route path="streams">{streamRoutes}</Route>
          <Route path="pipelines">{pipelineRoutes}</Route>
          <Route path="secrets">{secretRoutes}</Route>
        </Route>
        <Route path="pipelines">{flinkUiRoute}</Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function AuthCallback() {
  const { isLoading, error } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && error) {
      const auth0ErrorParams = new URLSearchParams(window.location.search);
      navigate(`/-/auth-error/?${auth0ErrorParams.toString()}`, {
        replace: true,
      });
    }
  }, [isLoading, error]);

  return null;
}

function NotFound() {
  return (
    <>
      <TopBar />
      <Fallback
        title="404"
        message="You look lost. Let's get you back home."
        actions={<HomeButton />}
      />
    </>
  );
}
